export type RatePlanMatchRequest = {
  packageId: string;
  packageName: string;
  /** The rate plans applied to the package's room -- the only valid answers. */
  ratePlanNames: Array<string>;
  roomName?: string;
};

export type RatePlanMatchConfidence = 'high' | 'low';

export type RatePlanMatchEntry = {
  confidence: RatePlanMatchConfidence;
  packageId: string;
  ratePlanName: string | null;
};

export const RATE_PLAN_MATCH_SYSTEM_PROMPT = [
  "Pick the rate plan each hotel package (a Salesforce Deal Option) is sold under, from the rate plans applied to the package's room.",
  'Rate plan names look like "Flash Sep 26", optionally with tags: an occupancy ("1A", "2A2C", "2A+"), a length of stay ("3N", "5N+", "7N"), or a variant such as board or an upgrade ("AI", "HB", "FB", "High Tea Upgrade", "Cruise").',
  'Package names carry the same details: occupancy as adults/children (1A, 2A, 2A2C, 4A2C, or "4 pax"), the number of nights (3N, "5 Nights"), and variant words.',
  'Choose the rate plan whose tags all match the package. A plan with no tag in a category is the default for that category: no occupancy tag means 2A, no length-of-stay tag means any nights, no variant tag means no variant.',
  'Only choose a tagged rate plan when the package name shows that tag. "5N+" and "7N+" cover that many nights or more. "Solo" in a package name means 1A. Transfers, room view and other words in the package name do not affect the rate plan.',
  'Set confidence to "high" only when exactly one rate plan clearly fits. Otherwise set confidence to "low", and use null when no rate plan fits at all. Always answer with the exact rate plan name from the list given for that package.',
].join('\n');

export const RATE_PLAN_MATCH_RESPONSE_SCHEMA = {
  type: 'object',
  properties: {
    matches: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          packageId: { type: 'string' },
          ratePlanName: { type: ['string', 'null'] },
          confidence: { type: 'string', enum: ['high', 'low'] },
        },
        required: ['packageId', 'ratePlanName', 'confidence'],
        additionalProperties: false,
      },
    },
  },
  required: ['matches'],
  additionalProperties: false,
};

export function buildRatePlanMatchUserPrompt(requests: Array<RatePlanMatchRequest>) {
  return [
    'Match each package to one of its rate plans. Return one entry per package id.',
    `Packages: ${JSON.stringify(
      requests.map(({ packageId, packageName, ratePlanNames, roomName }) => ({
        packageId,
        packageName,
        ratePlanNames,
        ...(roomName ? { roomName } : {}),
      })),
    )}`,
  ].join('\n');
}

export function parseRatePlanMatchResponse(content: string): Array<RatePlanMatchEntry> | null {
  try {
    const matches = JSON.parse(content)?.matches;

    if (!Array.isArray(matches)) {
      return null;
    }

    return matches.filter(
      (entry): entry is RatePlanMatchEntry =>
        !!entry &&
        typeof entry.packageId === 'string' &&
        (entry.ratePlanName === null || typeof entry.ratePlanName === 'string') &&
        (entry.confidence === 'high' || entry.confidence === 'low'),
    );
  } catch {
    return null;
  }
}
